import axios, { AxiosError } from "axios";
import { API_ENDPOINTS } from '../../apiConfig';

/** ==============================
 *  DTOs / Types for KPI payload
 *  ============================== */

export type Uuid = string;

export interface GeneralTask {
  id: Uuid;
  title: string;
  billed: boolean;
  status: string;
  category: string;
  due_date: string | null;           // ISO string
  end_time: string | null;           // ISO string
  priority: "LOW" | "MEDIUM" | "HIGH" | string | null;
  entity_id: Uuid | null;
  is_active: boolean;
  task_type: string | null;
  tenant_id: Uuid;
  created_at: string | null;
  created_by: Uuid | null;
  is_deleted: boolean;
  start_time: string | null;
  task_level: number | null;
  updated_at: string | null;
  updated_by: Uuid | null;
  description: string | null;
  entity_type: string | null;
  is_billable: boolean;
  tenant_name: string | null;
  actual_hours: number | null;
  assigned_to_id: Uuid | null;
  parent_task_id: Uuid | null;
  estimated_hours: number | null;
  assigned_to_name: string | null;
}

export interface ProjectTask {
  id: Uuid;
  sl_no: number | null;
  title: string;
  units: string | null;
  status: string;
  comment: string | null;
  priority: string | null;
  progress: number | null;
  parent_id: Uuid | null;
  task_type: string | null;
  tenant_id: Uuid;
  created_at: string | null;
  created_by: Uuid | null;
  is_deleted: boolean;
  project_id: Uuid | null;
  updated_at: string | null;
  updated_by: Uuid | null;
  actual_rate: number | null;
  approved_by: Uuid | null;
  assigned_to: Uuid | null;
  contract_id: Uuid | null;
  description: string | null;
  is_approved: boolean | null;
  is_billable: boolean | null;
  rejected_at: string | null;
  rejected_by: Uuid | null;
  tenant_name: string | null;
  completed_by: Uuid | null;
  is_milestone: boolean | null;
  project_name: string | null;
  approval_date: string | null;
  cost_variance: number | null;
  estimated_rate: number | null;
  given_end_date: string | null;
  hierarchy_path: string | null;
  tax_percentage: number | null;
  actual_end_date: string | null;
  actual_quantity: number | null;
  is_cost_overrun: boolean | null;
  given_start_date: string | null;
  rejection_reason: string | null;
  actual_start_date: string | null;
  is_effort_overrun: boolean | null;
  estimated_quantity: number | null;
  termination_reason: string | null;
  actual_effort_hours: number | null;
  review_requested_at: string | null;
  review_requested_by: Uuid | null;
  effort_variance_hours: number | null;
  estimated_effort_hours: number | null;
  actual_total_tax_amount: number | null;
  estimated_total_tax_amount: number | null;
  actual_total_amount_with_tax: number | null;
  actual_total_amount_without_tax: number | null;
  estimated_total_amount_with_tax: number | null;
  estimated_total_amount_without_tax: number | null;
}

export interface OverdueItem {
  title: string;
  due_date: string; // may be YYYY-MM-DD or full ISO
  entity_id: Uuid;
  entity_type: "PROJECT_TASK" | "GENERAL_TASK" | string;
  days_overdue: number;
}

export interface RecentAssignment {
  id: Uuid;
  role: string;
  status: string;
  comments: string | null;
  entity_id: Uuid;
  assigned_at: string; // ISO
  entity_type: string;
}

export interface CountByEntityStatus {
  count: number;
  status: string;
  entity_id: Uuid;
  entity_type: string;
}

export interface TaskCountByRoleStatus {
  role: string;
  total: number;
  status: string;
}

export interface TaskSummaryByRoleStatus {
  role: string;
  count: number;
  status: string;
  entity_id: Uuid;
  entity_type: string;
}

export interface CompletionRateByRole {
  role: string;
  total: number;
  completed: number;
  completion_rate: number;
}

export interface PendingAssignedByMe {
  count: number;
}

export interface UserKpiResponse {
  general_tasks: GeneralTask[];
  overdue_tasks: OverdueItem[];
  project_tasks: ProjectTask[];
  other_assignments: any[];
  recent_assignments: RecentAssignment[];
  pending_assigned_by_me: PendingAssignedByMe;
  completion_rate_by_role: CompletionRateByRole[];
  counts_by_entity_status: CountByEntityStatus[];
  task_count_by_role_status: TaskCountByRoleStatus[];
  task_summary_by_role_status: TaskSummaryByRoleStatus[];
  my_top_priorities_next_7_days: Array<{
    title: string;
    due_date: string;
    priority: string | null;
    entity_id: Uuid;
    entity_type: string;
  }>;
  overdue_tasks_assignee_all_entities: OverdueItem[];
}

/** =========
 *  Defaults
 *  ========= */

// Safe empty payload we return on aborted calls so UI doesn’t render an error banner
export const EMPTY_USER_KPI: UserKpiResponse = {
  general_tasks: [],
  overdue_tasks: [],
  project_tasks: [],
  other_assignments: [],
  recent_assignments: [],
  pending_assigned_by_me: { count: 0 },
  completion_rate_by_role: [],
  counts_by_entity_status: [],
  task_count_by_role_status: [],
  task_summary_by_role_status: [],
  my_top_priorities_next_7_days: [],
  overdue_tasks_assignee_all_entities: [],
};

// Aborted-request detector (Axios v1 + fetch AbortSignal)
function isAbort(err: any): boolean {
  return (
    axios.isCancel?.(err) ||
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError"
  );
}

/** ================
 * API configuration
 * ================ */
const API_BASE = `${API_ENDPOINTS.project}/kpis`;
const api = axios.create({
  baseURL: API_BASE,
  timeout: 20000,
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    // If it was canceled/aborted, resolve with EMPTY so the UI doesn’t flash an error.
    if (isAbort(err)) {
      return Promise.resolve({ data: EMPTY_USER_KPI } as any);
    }
    // Otherwise, normalize error message
    const msg =
      (err.response?.data as any)?.message ||
      err.message ||
      "Network error while fetching KPIs";
    return Promise.reject(new Error(msg));
  }
);

export interface GetUserKpisParams {
  tenantId: Uuid;
  userId: Uuid;
  lookaheadDays?: number; // default 30 in BE
  lookbackWeeks?: number; // default 12 in BE
  signal?: AbortSignal;   // <- pass AbortSignal to allow cancel on route change / re-apply
}

/** Fetches the KPI payload from Spring Boot which proxies Postgres fn_user_kpis */
export async function getUserKpis(params: GetUserKpisParams): Promise<UserKpiResponse> {
  const { tenantId, userId, lookaheadDays, lookbackWeeks, signal } = params;
  const r = await api.get<UserKpiResponse>("/user", {
    params: { tenantId, userId, lookaheadDays, lookbackWeeks },
    signal, // <- important: lets us cancel on unmount or re-apply
  });
  return r.data as unknown as UserKpiResponse;
}

/** Helpers */
export function fmtDate(dt?: string | null) {
  if (!dt) return "-";
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return dt;
  return d.toLocaleString();
}

export function badgeColorByStatus(status?: string) {
  const s = (status || "").toUpperCase();
  if (s === "COMPLETED") return "bg-green-100 text-green-700 border-green-200";
  if (s === "ASSIGNED") return "bg-blue-100 text-blue-700 border-blue-200";
  if (s === "PLANNED") return "bg-amber-100 text-amber-700 border-amber-200";
  if (s === "DRAFT") return "bg-gray-100 text-gray-700 border-gray-200";
  if (s === "REOPENED") return "bg-red-100 text-red-700 border-red-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}

export function badgeColorByPriority(priority?: string | null) {
  const p = (priority || "").toUpperCase();
  if (p === "HIGH") return "bg-red-100 text-red-700 border-red-200";
  if (p === "MEDIUM") return "bg-amber-100 text-amber-700 border-amber-200";
  if (p === "LOW") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
}